/*
**  rui-form-field.js
**
**  rui-form-field component
*/
import { LitElement, html, css } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';
import { ruiIsDataDense } from '../common/rui-common-dom-detection.js';
export class RuiFormField extends LitElement {
    static get is() {
        return 'rui-form-field';
    }
    static get properties() {
        return {
            hasError: { type: Object },
            isRequired: { type: Object },
            orientation: { type: String },
            labelText: { type: String },
            labelFor: { type: String },
            labelClasses: { type: String },
            valueClasses: { type: String },
            inputPrefix: { type: String },
            inputSuffix: { type: String },
            actionButtonIconType: { type: String },
            helpText: { type: String }
        };
    }
    constructor() {
        super();
        this._hasError = false;
        this.isRequired = false;
        this.orientation = 'horizontal';
        this.labelText = '';
        this.labelFor = '';
        this.labelClasses = '';
        this.valueClasses = '';
        this.inputPrefix = '';
        this.inputSuffix = '';
        this.actionButtonIconType = '';
        this.helpText = '';
        this._infoTooltipSlotted = false;
    }
    // Custom setter for hasError property
    set hasError(newValue) {
        if (this._hasError !== newValue) {
            this._hasError = newValue;
            this.requestUpdate();
            // Look for the generic slot, the one that isn't the slot for the info-tooltip
            let index = 0;
            let found = false;
            const slots = this.shadowRoot.querySelectorAll('slot');
            while (index < slots.length && !found) {
                if (slots[index].id !== 'info-tooltip') {
                    found = true;
                }
                else {
                    index++;
                }
            }
            // If we find that slot, set or reset the error status on the slot's children
            if (found) {
                const nodes = slots[index].assignedNodes();
                for (let i = 0; i < nodes.length; i++) {
                    if (this._hasError) {
                        nodes[i].classList.add('rui-form-has-error');
                    }
                    else {
                        nodes[i].classList.remove('rui-form-has-error');
                    }
                }
            }
        }
    }
    // Custom getter for hasError property
    get hasError() {
        return this._hasError;
    }
    connectedCallback() {
        super.connectedCallback();
        setTimeout(() => {
            // Look for the slot for the info-tooltip
            let genericSlotIndex = -1;
            let infoTooltipSlotIndex = -1;
            const slots = this.shadowRoot.querySelectorAll('slot');
            for (let i = 0; i < slots.length; i++) {
                if (slots[i].id === 'info-tooltip') {
                    infoTooltipSlotIndex = i;
                }
                else {
                    genericSlotIndex = i;
                }
            }
            // If a field info tooltip is present, let all fields in the page
            // know about this by setting a data attribute on body
            if (infoTooltipSlotIndex >= 0 && slots[infoTooltipSlotIndex].assignedNodes().length > 0) {
                this._infoTooltipSlotted = true;
            }
            // Special processing for elements in the value slot
            // when there are input add-ons
            if (genericSlotIndex >= 0 &&
                slots[genericSlotIndex].assignedNodes().length > 0) {
                const nodes = slots[genericSlotIndex].assignedNodes();
                for (let i = 0; i < nodes.length; i++) {
                    try {
                        // Set/reset error styling on input
                        nodes[i].classList.remove('rui-form-has-error');
                    }
                    catch { }
                    // Set proper styling on input group elements
                    if (this.inputPrefix || this.inputSuffix || this.actionButtonIconType) {
                        const isFirstElement = !this.inputPrefix;
                        const isLastElement = !this.inputSuffix && !this.actionButtonIconType;
                        if (isFirstElement) {
                            nodes[i].classList.add('rui-form-input-group-first');
                        }
                        else {
                            nodes[i].classList.remove('rui-form-input-group-first');
                        }
                        if (isLastElement) {
                            nodes[i].classList.add('rui-form-input-group-last');
                        }
                        else {
                            nodes[i].classList.remove('rui-form-input-group-last');
                        }
                        if (!isFirstElement && !isLastElement) {
                            nodes[i].classList.add('rui-form-input-group-middle');
                        }
                        else {
                            nodes[i].classList.remove('rui-form-input-group-middle');
                        }
                    }
                }
            }
        }, 10);
        // Update after small delay to allow time for all fields to connect
        // so we can determine if a field info tooltip is present in any
        setTimeout(() => {
            this.requestUpdate();
        }, 20);
    }
    click(e) {
        e.preventDefault();
        e.stopPropagation();
        let clickEvent = new CustomEvent('rui-form-field-action-button-click', {
            detail: {},
            bubbles: true,
            composed: true
        });
        this.dispatchEvent(clickEvent);
    }
    render() {
        const isVertical = this.orientation.toLowerCase() === 'vertical';
        const labelClasses = this.labelClasses ? this.labelClasses : (isVertical ? 'col-sm-12' : 'col-md-4');
        const valueClasses = this.valueClasses ? this.valueClasses : (isVertical ? 'col-sm-12' : 'col-md-8');
        let labelText = this.labelText;
        if (!this.labelText.endsWith(':') && !this.labelText.endsWith('?')) {
            labelText += ':';
        }
        const containerClasses = {
            ['row']: true,
            ['rui-form-group']: true,
            ['rui-form-has-error']: this._hasError,
            ['rui-form-group-dense']: ruiIsDataDense()
        };
        const fieldLabelClasses = {
            ['rui-form-control-label']: true,
            ['rui-form-required-indicator']: this.isRequired
        };
        const infoTooltipClasses = {
            ['rui-form-field-info-tooltip']: true,
            ['rui-form-field-info-tooltip-visible']: this._infoTooltipSlotted
        };
        return html `
            <div class="${classMap(containerClasses)}">
                ${this.labelText ? html `
                    <div class="rui-form-control-label-container ${labelClasses}">
                        <label class="${classMap(fieldLabelClasses)}" for="${this.labelFor}">${labelText}</label>
                        <div class="${classMap(infoTooltipClasses)}">
                            <slot id="info-tooltip" name="info-tooltip"></slot>
                        </div>
                    </div>
                ` : html `<div class="rui-form-control-label-container ${labelClasses}"></div>`}
                ${isVertical && this.labelText ? html `</div><div class="row">` : ''}
                <div class="${valueClasses}">
                    <div class="rui-form-input-group">
                        ${this.inputPrefix ? html `<span class="rui-form-input-group-addon rui-form-input-group-first">${this.inputPrefix}</span>` : ''}
                        <slot></slot>
                        ${this.inputSuffix && this.actionButtonIconType ? html `<span class="rui-form-input-group-addon rui-form-input-group-middle">${this.inputSuffix}</span>` : ''}
                        ${this.inputSuffix && !this.actionButtonIconType ? html `<span class="rui-form-input-group-addon rui-form-input-group-last">${this.inputSuffix}</span>` : ''}
                        ${this.actionButtonIconType ? html `
                            <span class="rui-form-input-group-btn">
                                <rui-button iconType="${this.actionButtonIconType}" context="action-button" @rui-click="${this.click}"></rui-button>
                            </span>
                        ` : ''}
                    </div>
                    ${this.helpText ? html `<div class="rui-form-help-block">${this.helpText}</div>` : ''}
                </div>
            </div>
        `;
    }
    static get styles() {
        return css `
            .rui-form-field-info-tooltip {
                display: none;
            }
            
            .rui-form-field-info-tooltip.rui-form-field-info-tooltip-visible {
                display: inline-block;
                margin-left: 8px;
                width: 23px;
            }
            
            ::slotted(rui-info-tooltip),
            ::slotted(jha-field-tooltip) {
                display: inline-block;
                padding-top: 4px;
            }
            
            .rui-form-group {
                box-sizing: border-box;
                color: var(--text-regular, #181830);
                font-family: var(--font-family, "Segoe UI", "Open Sans", "Helvetica Neue", Arial, sans-serif);
                font-size: var(--font-size, 14px);
                font-weight: var(--font-weight, 400);
                margin-bottom: 15px;
                margin-top: 4px;
                min-height: 35px;
                user-select: text;
                -webkit-font-smoothing: antialiased;
            }
            
            .rui-form-group-dense {
                margin-bottom: 4px;
            }
            
            .rui-form-group.rui-form-has-error {
                margin-bottom: 0;
            }
            
            .rui-form-control {
                background-color: var(--content-primary-bg, #fff);
                border: 1px solid var(--line, #dbe4eb);
                border-radius: 4px;
                box-shadow: var(--box-shadow-form-control, inset 0 1px 1px rgba(0, 0, 0, .075));
                box-sizing: border-box;
                color: var(--text-regular, #181830);
                display: block;
                font-family: var(--font-family, "Segoe UI", "Open Sans", "Helvetica Neue", Arial, sans-serif);
                font-size: 14px;
                margin: 0;
                padding: 6px 12px;
                width: 100%;
            }
            
            input.rui-form-controll {
                height: 32px;
            }
            
            body[data-rui-dense] .rui-form-control {
                font-size: 13px;
            }
            
            .rui-form-control:focus {
                outline: var(--outline-width, 1px) var(--outline-style, solid) var(--btn-primary-border, #3878ab);
                outline-offset: var(--outline-offset-btn, 0);
            }
            
            .rui-form-has-error .rui-form-control:focus {
                outline: var(--outline-width, 1px) var(--outline-style, solid) var(--text-error, #e13329);
                outline-offset: var(--outline-offset-btn, 0);
            }
            
            select.rui-form-control {
                appearance: none;
                -moz-appearance: none;
                -webkit-appearance: none;
                background-image: url("data:image/svg+xml;utf8,<svg version='1.1' xmlns='http://www.w3.org/2000/svg' width='15' height='15' viewBox='0 0 15 15' fill='%23000000'><path d='M256 377.6l-201.6-201.6 28.8-28.8 172.8 172.8 172.8-172.8 28.8 28.8z'></path></svg>");
                background-position: top 9px right 6px;
                background-repeat: no-repeat;
                background-size: 15px;
                padding-right: 28px;
            }
            
            .rui-form-input-highlighted.form-control {
                background-color: #ffffde;
                color: #181830;
            }

            .rui-form-control-label-container {
                display: flex;
            }
            
            .rui-form-control-label {
                color: var(--text-regular, #181830);
                display: inline-block;
                font-weight: 400;
                line-height: 17px;
                margin: 8px 0 5px 0;
                max-width: 100%;
                text-align: left;
                vertical-align: middle;
            }
            
            .rui-form-input-group {
                border-collapse: separate;
                box-sizing: border-box;
                display: table;
                position: relative;
                user-select: text;
                width: 100%;
            }
            
            .rui-form-input-group-addon {
                background-color: var(--well-bg, #f2f5f8);
                border: 1px solid var(--line, #dbe4eb);
                border-collapse: separate;
                border-bottom-left-radius: 4px;
                border-bottom-right-radius: 0;
                border-right-width: 0;
                border-top-left-radius: 4px;
                border-top-right-radius: 0;
                box-sizing: border-box;
                color: var(--text-regular, #181830);
                display: table-cell;
                height: 32px;
                padding: 0 12px;
                text-align: center;
                vertical-align: middle;
                white-space: nowrap;
                width: 1%;
            }
            
            .rui-form-input-group-middle {
                border-bottom-left-radius: 0;
                border-bottom-right-radius: 0;
                border-top-left-radius: 0;
                border-top-right-radius: 0;
            }
            
            .rui-form-input-group-first {
                border-bottom-right-radius: 0;
                border-bottom-left-radius: 4px;
                border-top-right-radius: 0;
                border-top-left-radius: 4px;
            }
            
            .rui-form-input-group-last {
                border-bottom-right-radius: 4px;
                border-bottom-left-radius: 0;
                border-left-width: 0;
                border-right-width: 1px;
                border-top-right-radius: 4px;
                border-top-left-radius: 0;
            }
            
            .rui-form-input-group-btn {
                border: none;
                border-collapse: separate;
                box-sizing: border-box;
                display: table-cell;
                font-size: 0;
                position: relative;
                user-select: text;
                vertical-align: middle;
                width: 1%;
                white-space: nowrap;
            }
            
            .rui-text-input-right {
                text-align: right;
            }
            
            .rui-text-input-center {
                text-align: center;
            }
            
            .rui-text-input-left {
                text-align: left;
            }
            
            .rui-form-help-block {
                box-sizing: border-box;
                color: var(--text-help, rgba(24, 24, 48, 0.5));
                display: block;
                font-style: italic;
                margin-top: 5px;
                word-wrap: break-word;
                user-select: text;
                -webkit-font-smoothing: antialiased;
                -webkit-tap-highlight-color: var(--text-regular, #181830);
                -webkit-text-size-adjust: 100%;
            }
            
            .rui-form-has-error,
            .rui-form-has-error .rui-form-help-block {
                color: var(--text-error, #e13329);
            }
            
            .rui-form-has-error .rui-form-control-label {
                color: var(--text-error, #e13329);
                font-weight: 700;
            }
            
            .rui-form-has-error .rui-form-control {
                border-color: var(--text-error, #e13329);
            }
            
            .rui-form-has-error .rui-form-control,
            .rui-form-has-error .rui-form-control:enabled:hover,
            .rui-form-has-error.rui-form-control,
            .rui-form-has-error.rui-form-control:enabled:hover {
                border-color: var(--text-error, #e13329);
            }
            
            .rui-form-control[disabled],
            .rui-form-control[readonly],
            fieldset[disabled] .rui-form-control {
                background-color: var(--form-disabled-bg, #eee);
                color: var(--form-disabled-fg, #999);
            }
            
            .rui-form-required-indicator:after {
                content: '*';
                color: var(--form-required-field, #f81);
                font-family: Tahoma, Arial, Helvetica, sans-serif;
                font-size: 8.25pt;
                font-weight: 700;
                line-height: 13px;
                padding-left: 4px;
            }
            
            /* ------ Copied in from rui-responsive-layout.scss ------ */
            
            .row {
                display: grid;
                grid-template-columns: repeat(12, 1fr);
                gap: 0 10px;
            }
            
            .row > *:not([class^="col-xs-"]) {
                grid-column: span 12;
            }
            
            .row .col-xs-12 {
                grid-column: span 12;
            }
            
            .row .col-xs-11 {
                grid-column: span 11;
            }
            
            .row .col-xs-10 {
                grid-column: span 10;
            }
            
            .row .col-xs-9 {
                grid-column: span 9;
            }
            
            .row .col-xs-8 {
                grid-column: span 8;
            }
            
            .row .col-xs-7 {
                grid-column: span 7;
            }
            
            .row .col-xs-6 {
                grid-column: span 6;
            }
            
            .row .col-xs-5 {
                grid-column: span 5;
            }
            
            .row .col-xs-4 {
                grid-column: span 4;
            }
            
            .row .col-xs-3 {
                grid-column: span 3;
            }
            
            .row .col-xs-2 {
                grid-column: span 2;
            }
            
            .row .col-xs-1 {
                grid-column: span 1;
            }
            
            @media (min-width: 768px) {
                .row .col-sm-12 {
                    grid-column: span 12;
                }
            
                .row .col-sm-11 {
                    grid-column: span 11;
                }
            
                .row .col-sm-10 {
                    grid-column: span 10;
                }
            
                .row .col-sm-9 {
                    grid-column: span 9;
                }
            
                .row .col-sm-8 {
                    grid-column: span 8;
                }
            
                .row .col-sm-7 {
                    grid-column: span 7;
                }
            
                .row .col-sm-6 {
                    grid-column: span 6;
                }
            
                .row .col-sm-5 {
                    grid-column: span 5;
                }
            
                .row .col-sm-4 {
                    grid-column: span 4;
                }
            
                .row .col-sm-3 {
                    grid-column: span 3;
                }
            
                .row .col-sm-2 {
                    grid-column: span 2;
                }
            
                .row .col-sm-1 {
                    grid-column: span 1;
                }
            }
            
            @media (min-width: 992px) {
                .row .col-md-12 {
                    grid-column: span 12;
                }
            
                .row .col-md-11 {
                    grid-column: span 11;
                }
            
                .row .col-md-10 {
                    grid-column: span 10;
                }
            
                .row .col-md-9 {
                    grid-column: span 9;
                }
            
                .row .col-md-8 {
                    grid-column: span 8;
                }
            
                .row .col-md-7 {
                    grid-column: span 7;
                }
            
                .row .col-md-6 {
                    grid-column: span 6;
                }
            
                .row .col-md-5 {
                    grid-column: span 5;
                }
            
                .row .col-md-4 {
                    grid-column: span 4;
                }
            
                .row .col-md-3 {
                    grid-column: span 3;
                }
            
                .row .col-md-2 {
                    grid-column: span 2;
                }
            
                .row .col-md-1 {
                    grid-column: span 1;
                }
            }
            
            @media (min-width: 1200px) {
                .row .col-lg-12 {
                    grid-column: span 12;
                }
            
                .row .col-lg-11 {
                    grid-column: span 11;
                }
            
                .row .col-lg-10 {
                    grid-column: span 10;
                }
            
                .row .col-lg-9 {
                    grid-column: span 9;
                }
            
                .row .col-lg-8 {
                    grid-column: span 8;
                }
            
                .row .col-lg-7 {
                    grid-column: span 7;
                }
            
                .row .col-lg-6 {
                    grid-column: span 6;
                }
            
                .row .col-lg-5 {
                    grid-column: span 5;
                }
            
                .row .col-lg-4 {
                    grid-column: span 4;
                }
            
                .row .col-lg-3 {
                    grid-column: span 3;
                }
            
                .row .col-lg-2 {
                    grid-column: span 2;
                }
            
                .row .col-lg-1 {
                    grid-column: span 1;
                }
            }
            @media print {
                .rui-form-control,
                .rui-form-group,
                .rui-form-control-label,
                .rui-form-required-indicator:after {
                    background-color: transparent;
                    border-color: #000000;
                    color: #000000;
                }
            
                .rui-form-input-group-addon {
                    background-color: #eee;
                    color: #000000;
                }

                            
                .rui-form-input-group-addon {
                    background-color: #eeeeee;
                    border-color: #000000;
                }
            }
        `;
    }
}
customElements.get(RuiFormField.is) || customElements.define(RuiFormField.is, RuiFormField);
